// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	VOICE_ROOM_THEME_MANIFEST,
	type VoiceRoomThemeManifestEntry,
} from '@fluxer/constants/src/VoiceRoomThemeManifest';

// What a 3D room may spend on this device. Content (characters, baked rooms, clips, sound) is the same on every
// tier; only pixel density, effects, idle motion and texture size change.

export type RoomQualityTier = 'low' | 'medium' | 'high';
export type RoomQualitySetting = 'auto' | RoomQualityTier;

export interface RoomQualityProfile {
	tier: RoomQualityTier;
	pixelRatioCap: number;
	// 'tint': unlit characters with the room tint; 'lit': hemisphere + one directional light; 'lit_rim': lit plus a rim.
	characterLighting: 'tint' | 'lit' | 'lit_rim';
	contactShadows: boolean;
	// Steps per second of idle body motion while the window has focus; 0 keeps an idle room at zero frames.
	idleMotionHz: number;
	// Steps per second of ambient room motion (dust, light chain, TV glow); 0 turns it off.
	ambientMotionHz: number;
	bloom: boolean;
	textureSize: 1024 | 2048;
	// The room's baked textures seen at a slant (a wide floor from the follow camera): anisotropic filtering, capped by
	// the GPU.
	textureAnisotropy: 1 | 4 | 8;
	// The fine grain a room lays over its ground (contract rev 4.9 detail_m), sharp where the baked atlas is magnified.
	groundDetail: boolean;
	characterLodDistanceM: number;
	maxCharacters: number;
	// Hard frame cap for the tier; null leaves the frame-rate setting in charge.
	frameCap: 30 | null;
}

const PROFILES: Record<RoomQualityTier, RoomQualityProfile> = {
	low: {
		tier: 'low',
		pixelRatioCap: 1,
		characterLighting: 'tint',
		contactShadows: true,
		idleMotionHz: 0,
		ambientMotionHz: 0,
		bloom: false,
		textureSize: 1024,
		textureAnisotropy: 1,
		groundDetail: false,
		characterLodDistanceM: 10,
		maxCharacters: 32,
		frameCap: 30,
	},
	medium: {
		tier: 'medium',
		pixelRatioCap: 1.5,
		characterLighting: 'lit',
		contactShadows: true,
		idleMotionHz: 10,
		ambientMotionHz: 10,
		bloom: false,
		textureSize: 2048,
		textureAnisotropy: 4,
		groundDetail: true,
		characterLodDistanceM: 12,
		maxCharacters: 48,
		frameCap: null,
	},
	high: {
		tier: 'high',
		pixelRatioCap: 2,
		characterLighting: 'lit_rim',
		contactShadows: true,
		idleMotionHz: 20,
		ambientMotionHz: 20,
		bloom: true,
		textureSize: 2048,
		textureAnisotropy: 8,
		groundDetail: true,
		characterLodDistanceM: 18,
		maxCharacters: 48,
		frameCap: null,
	},
};

export function roomQualityProfile(tier: RoomQualityTier): RoomQualityProfile {
	return PROFILES[tier];
}

const TIERS: ReadonlyArray<RoomQualityTier> = ['low', 'medium', 'high'];
// Software rasterisers start low; GPU families with room to spare start high; every other GPU (Intel, Mali, Adreno,
// Apple M1 and anything unnamed) starts medium.
const SOFTWARE_GPU = /swiftshader|llvmpipe/i;
const HIGH_GPU = /geforce|rtx|radeon rx|apple m\d+ (?:pro|max|ultra)/i;
// Cores and GB of memory at or under which a device starts one tier lower.
const SMALL_DEVICE = 4;

export interface RoomQualityDevice {
	renderer: string | null;
	hardwareConcurrency: number | null;
	deviceMemory: number | null;
}

export function lowerTier(tier: RoomQualityTier): RoomQualityTier {
	return TIERS[Math.max(0, TIERS.indexOf(tier) - 1)]!;
}

const knownTier = (value: unknown): RoomQualityTier | null => TIERS.find((tier) => tier === value) ?? null;

// The tier 'auto' starts this device at. The GPU name is read here only, never logged or sent anywhere.
export function resolveQualityTier(device: RoomQualityDevice): RoomQualityTier {
	const gpu = device.renderer ?? '';
	const tier = SOFTWARE_GPU.test(gpu) ? 'low' : HIGH_GPU.test(gpu) ? 'high' : 'medium';
	const small =
		(device.hardwareConcurrency ?? Infinity) <= SMALL_DEVICE || (device.deviceMemory ?? Infinity) <= SMALL_DEVICE;
	return small ? lowerTier(tier) : tier;
}

// A chosen tier as it is; 'auto' (or a value this build does not know) is the device's tier, never above the tier
// it fell to while drawing.
export function effectiveQualityTier(
	settings: {quality: RoomQualitySetting; autoQualityCap: RoomQualityTier | null},
	device: RoomQualityDevice,
): RoomQualityTier {
	const chosen = knownTier(settings.quality);
	if (chosen) return chosen;
	const tier = resolveQualityTier(device);
	const cap = knownTier(settings.autoQualityCap);
	return cap && TIERS.indexOf(cap) < TIERS.indexOf(tier) ? cap : tier;
}

// The room file for a tier: on a 1024 texture tier the manifest's 1024 variant of that room when it has one.
export function roomFileForTier(url: string, profile: RoomQualityProfile): string {
	if (profile.textureSize !== 1024) return url;
	const rooms: ReadonlyArray<VoiceRoomThemeManifestEntry> = Object.values(VOICE_ROOM_THEME_MANIFEST);
	for (const room of rooms) {
		const variant = room.variants?.[1024];
		if (variant && url.endsWith(`/${room.file}`)) return `${url.slice(0, -room.file.length)}${variant}`;
	}
	return url;
}
